import { useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { TESTIMONIALS, TESTIMONIALS_INTRO } from "@/lib/content";
import { clientLogo } from "@/lib/assets";

export default function Testimonials() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start", slidesToScroll: 1 });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    onSelect();
    // Auto-play
    timerRef.current = setInterval(() => emblaApi.scrollNext(), 7000);
    return () => {
      emblaApi.off("select", onSelect);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [emblaApi]);

  return (
    <section id="testimonials" data-testid="testimonials-section" className="relative bg-white py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="max-w-3xl mb-14">
          <div className="flex items-center gap-3 mb-5">
            <span className="gold-hairline" />
            <span className="text-gold text-xs uppercase tracking-[0.28em] font-medium">Testimonials</span>
          </div>
          <h2 className="font-serif text-navy text-3xl md:text-4xl lg:text-5xl font-light leading-[1.1] tracking-[-0.01em]">
            In the <em className="text-gold not-italic">words</em> of our clients.
          </h2>
          <p className="text-mutedink text-base md:text-lg mt-6 font-light leading-relaxed">
            {TESTIMONIALS_INTRO}
          </p>
        </div>

        <div className="relative">
          <div className="overflow-hidden" ref={emblaRef} data-testid="testimonials-carousel">
            <div className="flex">
              {TESTIMONIALS.map((t, i) => (
                <article
                  key={i}
                  data-testid={`testimonial-${i}`}
                  className="flex-[0_0_100%] md:flex-[0_0_60%] lg:flex-[0_0_50%] min-w-0 pr-6 md:pr-10"
                >
                  <div className="relative bg-offwhite border-l-2 border-gold p-10 md:p-14 h-full">
                    <Quote size={44} strokeWidth={1} className="text-gold/25 absolute top-8 right-8" />

                    {/* Client logo (or text-only placeholder if none yet) */}
                    <div className="mb-6 h-[48px] md:h-[56px] flex items-center" data-testid={`testimonial-logo-${i}`}>
                      {t.logo_slug ? (
                        <img
                          src={clientLogo(t.logo_slug)}
                          alt={t.company}
                          className="max-h-full w-auto object-contain"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        // TODO: replace with official Holiday Dreamz logo when supplied
                        <span className="font-serif text-navy/70 text-xl italic tracking-wide">
                          {t.company}
                        </span>
                      )}
                    </div>

                    <p className="font-serif text-navy text-xl md:text-2xl leading-[1.55] font-light">
                      "{t.quote}"
                    </p>
                    <div className="mt-8 pt-6 border-t border-softgray flex items-center gap-3">
                      <span className="gold-hairline" />
                      <div>
                        <div className="text-navy font-semibold text-sm tracking-wide" data-testid={`testimonial-company-${i}`}>
                          {t.company}
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>

          {/* Controls */}
          <div className="mt-10 flex items-center justify-between">
            <div className="flex items-center gap-2 flex-wrap max-w-[70%]">
              {scrollSnaps.map((_, i) => (
                <button key={i}
                        aria-label={`Go to testimonial ${i + 1}`}
                        onClick={() => emblaApi?.scrollTo(i)}
                        data-testid={`testimonial-dot-${i}`}
                        className={`h-[2px] transition-all duration-500 ${selectedIndex === i ? "w-8 bg-gold" : "w-4 bg-softgray hover:bg-mutedink"}`} />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button aria-label="Previous testimonial"
                      onClick={() => emblaApi?.scrollPrev()}
                      data-testid="testimonial-prev"
                      className="w-11 h-11 border border-softgray flex items-center justify-center text-navy hover:border-gold hover:text-gold transition-colors">
                <ChevronLeft size={18} strokeWidth={1.5} />
              </button>
              <button aria-label="Next testimonial"
                      onClick={() => emblaApi?.scrollNext()}
                      data-testid="testimonial-next"
                      className="w-11 h-11 border border-softgray flex items-center justify-center text-navy hover:border-gold hover:text-gold transition-colors">
                <ChevronRight size={18} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>

        {/* SEO fallback: all quotes visible as accessible text */}
        <div className="sr-only" aria-hidden="false">
          <h3>All client testimonials</h3>
          <ul>
            {TESTIMONIALS.map((t, i) => (
              <li key={i}><strong>{t.company}:</strong> {t.quote}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
